import React from 'react';
import { Avatar, Space, Tooltip, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getEffectiveAvatarUrl } from '../../utils/avatar';

const { Text } = Typography;

export interface UserDisplayProps {
  userId?: string;
  user?: {
    id: string;
    name?: string | null;
    email?: string | null;
    avatarUrl?: string | null;
    preferences?: Record<string, unknown> | null;
  };
  showAvatar?: boolean;
  showEmail?: boolean;
  showTooltip?: boolean;
  linkToProfile?: boolean;
  size?: 'small' | 'default' | 'large';
  style?: React.CSSProperties;
}

/**
 * ユーザー情報を統一的に表示するコンポーネント
 *
 * @example
 * // 基本的な使用
 * <UserDisplay user={user} />
 *
 * @example
 * // ツールチップ付き
 * <UserDisplay user={user} showTooltip />
 *
 * @example
 * // プロフィールへのリンク付き
 * <UserDisplay user={user} linkToProfile />
 */
export function UserDisplay({
  userId,
  user,
  showAvatar = true,
  showEmail = false,
  showTooltip = true,
  linkToProfile = false,
  size = 'default',
  style
}: UserDisplayProps): JSX.Element | null {
  const navigate = useNavigate();

  // userオブジェクトまたはuserIdが必要
  if (!user && !userId) {
    return null;
  }

  const effectiveUser = user || { id: userId!, name: null, email: null, avatarUrl: null, preferences: null };
  const displayName = effectiveUser.name || effectiveUser.email || effectiveUser.id;
  const tooltipTitle = effectiveUser.email || effectiveUser.id;
  const avatarSrc = getEffectiveAvatarUrl(
    effectiveUser as { avatarUrl?: unknown; preferences?: unknown },
  );

  // アバターサイズ（より小さく調整）
  const avatarSize = size === 'small' ? 20 : size === 'large' ? 32 : 24;

  const handleClick = () => {
    if (linkToProfile && effectiveUser.id) {
      navigate(`/app/users/${effectiveUser.id}`);
    }
  };

  const content = (
    <Space size={8} style={{ cursor: linkToProfile ? 'pointer' : 'default', ...style }}>
      {showAvatar && (
        <Avatar
          size={avatarSize}
          src={avatarSrc ?? undefined}
          icon={!avatarSrc && <UserOutlined />}
        />
      )}
      {linkToProfile ? (
        <Text onClick={handleClick} style={{ fontSize: size === 'small' ? 12 : 14, cursor: 'pointer' }}>
          {displayName}
        </Text>
      ) : (
        <Text style={{ fontSize: size === 'small' ? 12 : 14 }}>{displayName}</Text>
      )}
      {showEmail && effectiveUser.email && effectiveUser.name && (
        <Text type="secondary" style={{ fontSize: size === 'small' ? 11 : 12 }}>
          ({effectiveUser.email})
        </Text>
      )}
    </Space>
  );

  if (showTooltip && effectiveUser.email && effectiveUser.email !== displayName) {
    return <Tooltip title={tooltipTitle}>{content}</Tooltip>;
  }

  return content;
}
